// src/hooks/useInsurancePolicies.ts

import { useCallback } from "react";

import type {
  ActivityLog,
  Policy,
} from "../contexts/InsuranceContextType";

interface UseInsurancePoliciesOptions {
  policies: Policy[];

  setPolicies: React.Dispatch<
    React.SetStateAction<Policy[]>
  >;

  logActivity: (
    activity: Omit<
      ActivityLog,
      "id" | "timestamp"
    >
  ) => void;
}

interface UseInsurancePoliciesReturn {
  addPolicy: (
    policyData: Omit<
      Policy,
      "id" | "createdAt" | "updatedAt"
    >
  ) => Promise<void>;

  updatePolicy: (
    id: string,
    updates: Partial<Policy>
  ) => Promise<void>;

  deletePolicy: (
    id: string
  ) => Promise<void>;

  renewPolicy: (
    id: string,
    newEndDate: string
  ) => Promise<void>;

  getPlanById: (
    id: string
  ) => Policy | undefined;

  updatePlan: (
    id: string,
    updates: Partial<Policy>
  ) => Promise<void>;
}

const createPolicyId = (): string =>
  `POL-${Date.now()}`;

export function useInsurancePolicies({
  policies,
  setPolicies,
  logActivity,
}: UseInsurancePoliciesOptions): UseInsurancePoliciesReturn {
  const addPolicy = useCallback(
    async (
      policyData: Omit<
        Policy,
        "id" | "createdAt" | "updatedAt"
      >
    ) => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        },
    [logActivity, setPolicies]
  );

  const updatePolicy = useCallback(
    async (
      id: string,
      updates: Partial<Policy>
    ) => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        },
    [logActivity, setPolicies]
  );

  const deletePolicy = useCallback(
    async (id: string) => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        },
    [logActivity, setPolicies]
  );

  const renewPolicy = useCallback(
    async (
      id: string,
      newEndDate: string
    ) => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        },
    [logActivity, setPolicies]
  );

  const getPlanById = useCallback(
    (id: string) =>
      policies.find(
        (policy) => policy.id === id
      ),
    [policies]
  );

  const updatePlan = useCallback(
    async (
      id: string,
      updates: Partial<Policy>
    ) => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        },
    [updatePolicy]
  );

  return {
    addPolicy,
    updatePolicy,
    deletePolicy,
    renewPolicy,
    getPlanById,
    updatePlan,
  };
}